import { useEffect, useMemo, useState } from "react";
import { get } from "lodash";
import * as hotjar from "analytics/hotjar";
import { useSegment } from "analytics/segment";
import { setCurrentUser } from "auth/actions";
import { useStore as useAuthStore } from "auth/store";
import {
  useGetAccessToken,
  useHandleAuthenticationError,
} from "auth/utils/oktaAuth";
import { useErrorReporter } from "core/hooks/useErrorReporter";
import { useGetLoggedInUserQuery } from "core/queries/GetLoggedInUser";
import { mapUserDataToProps } from "core/queries/mappers";
import { setFilters } from "tasks/actions";
import { useStore as useTasksStore } from "tasks/store";

export const useLoggedInUser = () => {
  const authToken = useGetAccessToken();
  const { handleAuthenticationError } = useHandleAuthenticationError();
  const [token, setToken] = useState(authToken);
  const authStore = useAuthStore();
  const tasksStore = useTasksStore();
  const { identify } = useSegment();
  const errorReporter = useErrorReporter();
  // Fetch user metadata
  const { data, error, loading } = useGetLoggedInUserQuery({
    skip: !token,
  });
  const user = useMemo(
    () => data && data.user && mapUserDataToProps(data.user),
    [data, error, loading],
  );

  // Update app state with current user
  const userId = get(authStore, "state.user.id", null);
  useEffect(() => {
    if (user && userId !== user.id) {
      authStore.dispatch(setCurrentUser(user));
      tasksStore.dispatch(setFilters({ city: user.cityId }));
      // Set Segment common props for the logged-in user
      identify(user);
      // Attach User info to errorReporter
      errorReporter.setUser({
        id: user.id,
        email: user.email,
        name: user.name,
      });
      hotjar.initialize(user);
    }
  }, [user && user.id]);

  // Unable to get user metadata, log user out and redirect to login
  if (error) {
    errorReporter.notify(error);
    handleAuthenticationError();
  }

  return { user, error, loading, token, setToken };
};
